import { JobApi, JobApiAxiosParamCreator } from "@eai-toolkit/client";
import type { HttpException } from "@store/api/exceptions";
import {
  axiosInstance,
  basePath,
  makeToolkitRequest,
  parseLogLines,
  webSocketTrimmedLinesStream,
} from "@store/api/utils";

const jobApi = new JobApi(undefined, undefined, axiosInstance);
const jobApiParamsCreator = JobApiAxiosParamCreator();

async function getJobLogsPath(opts: {
  jobId: string;
  runId: string;
  follow: boolean;
}) {
  const { jobId, runId, follow } = opts;
  const { url } = await jobApiParamsCreator.v1JobLogsRunGet(
    jobId,
    runId,
    follow ? 1 : undefined,
  );
  return url;
}

async function createJobLogsWebSocket(opts: {
  jobId: string;
  runId: string;
  follow: boolean;
}) {
  const { protocol, host } = new URL(basePath);
  let websocketHost = host;
  let websocketProtocol = protocol === "https:" ? "wss:" : "ws:";
  if (process.env.NODE_ENV === "development") {
    // In development, websockets need to connect to the backend server directly
    // since Create React App proxy doesn't handle websockets automatically
    websocketHost = "127.0.0.1:8000";
    websocketProtocol = "ws:";
  }
  const path = await getJobLogsPath(opts);
  return new WebSocket(`${websocketProtocol}//${websocketHost}/webapp${path}`);
}

function webSocketBlob(websocket: WebSocket): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const parts: BlobPart[] = [];
    let opened = false;
    websocket.addEventListener("open", () => {
      opened = true;
    });
    websocket.addEventListener("message", (event) => {
      parts.push(event.data);
    });
    websocket.addEventListener("error", reject, { once: true });
    websocket.addEventListener(
      "close",
      (event) => {
        if (!opened) {
          reject(new Error("Job log WebSocket closed before opening"));
          return;
        }
        if (event.code !== 1000) {
          const reason = event.reason ? `: ${event.reason}` : "";
          reject(
            new Error(
              `Job log WebSocket closed unexpectedly with code ${event.code}${reason}`,
            ),
          );
          return;
        }
        resolve(new Blob(parts, { type: "text/plain;charset=utf-8" }));
      },
      { once: true },
    );
  });
}

export async function getJobLogs(opts: { jobId: string; runId: string }) {
  const { jobId, runId } = opts;
  try {
    const { data } = await makeToolkitRequest<string>(() =>
      jobApi.v1JobLogsRunGet({ jobId, runId }),
    );
    return parseLogLines(data.trimEnd());
  } catch (err) {
    if ((err as HttpException).code !== 400) {
      throw err;
    }
    // A 400 means logs are still streaming.
    const websocket = await createJobLogsWebSocket({
      jobId,
      runId,
      follow: true,
    });
    // Start buffering stream immediately so we don't miss any messages.
    const logsStream = webSocketTrimmedLinesStream(websocket);
    // Wait for websocket to open to confirm running log updates are available.
    await new Promise((resolve, reject) => {
      websocket.addEventListener("open", resolve);
      websocket.addEventListener("error", reject);
      websocket.addEventListener("close", reject);
    });
    return logsStream;
  }
}

export async function getJobLogsBlob(opts: { jobId: string; runId: string }) {
  try {
    const path = await getJobLogsPath({ ...opts, follow: false });
    const { data } = await makeToolkitRequest<Blob>(() =>
      axiosInstance.get<Blob>(path, { responseType: "blob" }),
    );
    return data;
  } catch (err) {
    if ((err as HttpException).code !== 400) {
      throw err;
    }
    return webSocketBlob(
      await createJobLogsWebSocket({ ...opts, follow: false }),
    );
  }
}
